import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { PlacementSource, SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchInclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUninclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';
import { type BulkSpotlightTarget, type BulkSpotlightTransitionPlan } from '../utils/bulkSpotlightTransitions';
import {
  buildAdvertisingRefreshPredicate,
  getCurrentPlacementSource,
  updateAdvertisingCache,
} from '../utils/cacheHelpers';
import { useAwaitSpotlightOptimization } from './useAwaitSpotlightOptimization';

const INVALIDATION_META = {
  invalidates: ['getProperties', 'spotlightOverview'] as Array<string>,
};

type BulkSpotlightMutationResult = { listingAlias: string; success: boolean };
type BulkSpotlightMutationResponse = { results: Array<BulkSpotlightMutationResult> };

export const useSpotlightBatchMutations = () => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const awaitOptimization = useAwaitSpotlightOptimization();

  const { mutateAsync: batchExcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch exclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchIncludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchInclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch include listings as smart assign candidates' },
    },
  });

  const { mutateAsync: batchPinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch pin listings in spotlight' },
    },
  });

  const { mutateAsync: batchUnexcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unexclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchUnincludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUninclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch uninclude listings as smart assign candidates' },
    },
  });

  const { mutateAsync: batchUnpinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unpin listings from spotlight' },
    },
  });

  // INVALIDATION_META doesn't cover each row's per-listingAlias advertising query, so write the target
  // placementSource into it (immediate, immune to the staggered backend commit) then refetch for backend-owned fields.
  // The mutation already succeeded by this point, so a failed refetch is logged and swallowed rather than failing the
  // whole operation (which would leave the successful selections uncleared and surface an unhandled rejection).
  const settleAdvertisingCache = async (
    listingAliases: Array<ListingAlias>,
    placementSource: PlacementSource | null,
  ) => {
    listingAliases.forEach((listingAlias) => updateAdvertisingCache(queryClient, listingAlias, placementSource));
    // One invalidation, so rows sharing a page read it once.
    const settled = await Promise.allSettled([
      queryClient.invalidateQueries({ ...buildAdvertisingRefreshPredicate(listingAliases), refetchType: 'all' }),
    ]);
    settled.forEach((outcome) => {
      if (outcome.status === 'rejected') {
        logError(outcome.reason);
      }
    });
  };

  const executeBatch = async ({
    listingAliases,
    mutationFn,
    resultPlacementSource,
    successMessage,
    errorMessage,
    toastId,
  }: {
    listingAliases: Array<ListingAlias>;
    mutationFn: (aliases: Array<ListingAlias>) => Promise<BulkSpotlightMutationResponse>;
    resultPlacementSource: PlacementSource | null;
    successMessage: (successCount: number, failCount: number) => string;
    errorMessage: string;
    toastId: string;
  }) => {
    // Returned so callers can apply state that must only follow a confirmed success.
    let settled: Array<ListingAlias> = [];
    const priorSourceByAlias = new Map(listingAliases.map((a) => [a, getCurrentPlacementSource(queryClient, a)]));
    // optimistic update
    listingAliases.forEach((a) => updateAdvertisingCache(queryClient, a, resultPlacementSource));
    try {
      const response = await mutationFn(listingAliases);
      const succeededAliases = response.results.filter((r) => r.success).map((r) => r.listingAlias);
      const failedAliases = listingAliases.filter((a) => !succeededAliases.includes(a));
      failedAliases.forEach((a) => updateAdvertisingCache(queryClient, a, priorSourceByAlias.get(a) ?? null));
      await settleAdvertisingCache(succeededAliases, resultPlacementSource);
      if (succeededAliases.length > 0) {
        awaitOptimization();
      }
      settled = succeededAliases;
      const successCount = response.results.filter((r) => r.success).length;
      const failCount = response.results.length - successCount;
      enqueueToast(
        <Toast id={toastId} tone={failCount > 0 ? 'warning' : 'success'}>
          {successMessage(successCount, failCount)}
        </Toast>,
      );
    } catch (error) {
      listingAliases.forEach((a) => updateAdvertisingCache(queryClient, a, priorSourceByAlias.get(a) ?? null));
      enqueueToast(
        <Toast id={`${toastId}-error`} tone="critical">
          {errorMessage}
        </Toast>,
      );
      logError(error);
    }
    return settled;
  };

  const batchExclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchExcludeMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: PlacementSource.EXCLUDED,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} excluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} excluded from Spotlight`,
      errorMessage: 'Failed to exclude listings. Try again.',
      toastId: 'spotlight-batch-exclude',
    });

  // "Added to Smart Assign" is an opt-in, not a placement — the engine places it only when it ranks high enough.
  const batchInclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchIncludeMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: PlacementSource.INCLUDED,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} added to Smart Assign. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} added to Smart Assign`,
      errorMessage: 'Failed to add listings to Smart Assign. Try again.',
      toastId: 'spotlight-batch-include',
    });

  const batchPin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchPinMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: PlacementSource.PINNED,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} pinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} pinned to Spotlight`,
      errorMessage: 'Failed to pin listings. Try again.',
      toastId: 'spotlight-batch-pin',
    });

  const batchUnexclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnexcludeMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: null,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unexcluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize`,
      errorMessage: 'Failed to unexclude listings. Try again.',
      toastId: 'spotlight-batch-unexclude',
    });

  const batchUninclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnincludeMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: null,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} removed from Smart Assign. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} removed from Smart Assign`,
      errorMessage: 'Failed to remove listings from Smart Assign. Try again.',
      toastId: 'spotlight-batch-uninclude',
    });

  // Bypasses executeBatch: callers need the real success/failure to roll back, not a swallowed toast (RECE-2031).
  const revertPinnedToSmartAssign = async (listingAlias: ListingAlias) => {
    const response = await batchUnincludeMutation({ postData: { listingAliases: [listingAlias] } });
    if (!response.results.some((result) => result.success)) {
      throw new Error(`Failed to revert ${listingAlias} to Smart Assign`);
    }
    // refetchType: 'all' also refetches this row's advertising query once the loading-view shimmer unmounts it.
    await queryClient.invalidateQueries({ ...buildAdvertisingRefreshPredicate([listingAlias]), refetchType: 'all' });
    awaitOptimization();
  };

  const batchUnpin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnpinMutation({ postData: { listingAliases: aliases } }),
      resultPlacementSource: null,
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unpinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize`,
      errorMessage: 'Failed to unpin listings. Try again.',
      toastId: 'spotlight-batch-unpin',
    });

  const applyPlan = async (
    plan: BulkSpotlightTransitionPlan,
    target: BulkSpotlightTarget,
    smartConfigMode: SmartConfigMode,
    onSuccess?: (listingAliases: Array<string>) => void,
  ) => {
    const groups: Array<{
      aliases: Array<string>;
      resultPlacementSource: PlacementSource | null;
      mutationFn: (aliases: Array<string>) => Promise<BulkSpotlightMutationResponse>;
    }> = [
      {
        aliases: plan.aliasesByMutation.exclude,
        resultPlacementSource: PlacementSource.EXCLUDED,
        mutationFn: (aliases: Array<string>) => batchExcludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.include,
        resultPlacementSource: PlacementSource.INCLUDED,
        mutationFn: (aliases: Array<string>) => batchIncludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.pin,
        resultPlacementSource: PlacementSource.PINNED,
        mutationFn: (aliases: Array<string>) => batchPinMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.unexclude,
        resultPlacementSource: null,
        mutationFn: (aliases: Array<string>) => batchUnexcludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.uninclude,
        resultPlacementSource: null,
        mutationFn: (aliases: Array<string>) => batchUnincludeMutation({ postData: { listingAliases: aliases } }),
      },
    ].filter((group) => group.aliases.length > 0);

    const settled = await Promise.allSettled(groups.map((group) => group.mutationFn(group.aliases)));
    const results: Array<BulkSpotlightMutationResult> = [];
    const succeededResponses: Array<BulkSpotlightMutationResponse> = [];
    const cacheSettlements: Array<Promise<unknown>> = [];
    settled.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') {
        results.push(...outcome.value.results);
        const succeededAliases = outcome.value.results.filter((r) => r.success).map((r) => r.listingAlias);
        if (succeededAliases.length > 0) {
          succeededResponses.push(outcome.value);
          cacheSettlements.push(settleAdvertisingCache(succeededAliases, groups[index].resultPlacementSource));
        }
        return;
      }
      logError(outcome.reason);
      results.push(...groups[index].aliases.map((listingAlias) => ({ listingAlias, success: false })));
    });
    await Promise.all(cacheSettlements);

    const successCount = results.filter((result) => result.success).length;
    const failedCount = results.length - successCount;
    const successfulAliases = results.filter((result) => result.success).map((result) => result.listingAlias);
    if (successfulAliases.length > 0) {
      onSuccess?.(successfulAliases);
      awaitOptimization();
    }
    const targetLabel =
      target === 'PINNED'
        ? 'Pinned'
        : target === 'SMART_ASSIGN'
          ? 'Smart Assign'
          : smartConfigMode === SmartConfigMode.ALL_EXCLUDED
            ? 'Removed'
            : 'Excluded';
    const changedText = `${successCount} listing${successCount === 1 ? '' : 's'} moved to ${targetLabel}.`;
    const alreadyText = plan.alreadyAtTargetCount > 0 ? ` ${plan.alreadyAtTargetCount} already in that state.` : '';
    const skippedText =
      plan.managedByFeedSkippedCount > 0
        ? ` ${plan.managedByFeedSkippedCount} listing${
            plan.managedByFeedSkippedCount === 1 ? ' managed in its feed was' : 's managed in their feeds were'
          } skipped.`
        : '';
    const failedText = failedCount > 0 ? ` ${failedCount} could not be updated.` : '';
    const hasPartialResult = plan.managedByFeedSkippedCount > 0 || failedCount > 0;
    enqueueToast(
      <Toast id={`spotlight-batch-${target.toLowerCase()}`} tone={hasPartialResult ? 'warning' : 'success'}>
        {changedText + alreadyText + skippedText + failedText}
      </Toast>,
    );
    return failedCount === 0;
  };

  return {
    applyPlan,
    batchExclude,
    batchInclude,
    batchPin,
    batchUnexclude,
    batchUninclude,
    batchUnpin,
    revertPinnedToSmartAssign,
  };
};
